import React from "react";
import { X, ExternalLink } from "lucide-react";
import type { CarouselPublicationDoc } from "src/types/carouselPublication";

interface PublicationPreviewDialogProps {
  publication: CarouselPublicationDoc;
  onClose: () => void;
}

/**
 * Read-only preview of a past publication. Shows each frozen card exactly as
 * it was when the carousel was published.
 */
export const PublicationPreviewDialog: React.FC<
  PublicationPreviewDialogProps
> = ({ publication, onClose }) => {
  const cards = [...publication.cards].sort((a, b) => a.order - b.order);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-publication-title"
      className="fixed inset-0 z-[300] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <div
        className="relative max-h-full w-full overflow-y-auto rounded-t-[2rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:max-w-3xl sm:rounded-[2rem] sm:p-8"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="preview-publication-title"
              className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl"
            >
              Past carousel
            </h2>
            <p className="text-sm font-bold text-[#777777]">
              Published{" "}
              {new Date(publication.publishedAt.toMillis()).toLocaleString()}
              {" by "}
              {publication.publishedByDisplayName?.trim() ||
                publication.publishedBy}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-xl p-2 text-[#777777] hover:bg-[#F7F7F7] hover:text-[#3C3C3C]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ul className="flex flex-col gap-3">
          {cards.map((card) => (
            <li
              key={card.id}
              className="flex items-start gap-3 rounded-2xl border-2 border-[#E5E5E5] bg-white p-3 shadow-[0_2px_0_#D0D0D0]"
            >
              <div className="h-20 w-32 shrink-0 overflow-hidden rounded-xl border-2 border-[#E5E5E5] bg-[#F7F7F7]">
                <img
                  src={card.imageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-extrabold text-[#3C3C3C]">
                  {card.title}
                </p>
                {card.description && (
                  <p className="mt-0.5 line-clamp-2 text-xs font-semibold text-[#777777]">
                    {card.description}
                  </p>
                )}
                <a
                  href={card.targetLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-1 inline-flex max-w-full items-center gap-1 truncate font-mono text-[11px] font-bold text-[#6366F1] hover:underline"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">{card.targetLink}</span>
                </a>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span
                    className={
                      card.active
                        ? "rounded-md bg-[#ECFDF5] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#0F766E]"
                        : "rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#92400E]"
                    }
                  >
                    {card.active ? "Active" : "Paused"}
                  </span>
                  {card.startsAt && (
                    <span className="rounded-md bg-[#EDE9FE] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#6D28D9]">
                      Started{" "}
                      {new Date(card.startsAt.toMillis()).toLocaleDateString()}
                    </span>
                  )}
                  <span className="rounded-md bg-[#F1F5F9] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#475569]">
                    Was expiring{" "}
                    {new Date(card.expiresAt.toMillis()).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
